import React from 'react';
import { useSelector } from 'react-redux';
import { useShellAppContext } from '../features/shell/context/useShellAppContext';
import SearchExperienceEditor from '../features/search-experience-v2/editor/SearchExperienceEditor';
import SearchFeaturePageShell from '../components/shell/SearchFeaturePageShell';
import { useLocalizedMessage } from '../util/useLocalizedMessage';

/**
 * Search Experience v2 Settings Page
 *
 * This page provides a visual editor for configuring Search Experience v2 settings
 * including canvas layout, component settings, theme, and behavior.
 *
 * Shell surface: feature_page_shell / fluid
 * Width is fluid (max-width: 100%) because the v2 editor canvas requires
 * full, unconstrained viewport width.  This is intentionally wider than the
 * 'wide' preset (1280px) — it is not equivalent to any MUI Container preset.
 *
 * Availability contract (Option A — single-capability):
 *   settingsV2 is supplied by the product composition root.  Plugin mode
 *   enables it only for the complete shared-admin runtime contract; cloud
 *   mode enables it through its webservice adapter.
 *   There is no secondary page-level capability gate.  capabilities.searchExperience
 *   is not emitted by readPluginLane and is therefore never set; any check against
 *   it is dead code.
 *
 * Session-unavailable path:
 *   In iframe mode, ShellSessionGate (AppLayout) redirects to /embedded/app
 *   before this component renders, so the plugin runtime will normally be available.
 *   The guard below is the fallback for standalone mode, where ShellSessionGate
 *   passes through and the cloud runtime is selected by the composition root.
 */
const SearchExperienceV2SettingsPage = () => {
  const { currentShop } = useSelector((state) => state.app);
  const { searchSettingsClient: effectiveClient } = useShellAppContext();
  const localizeMessage = useLocalizedMessage();

  if (!effectiveClient) {
    return (
      <SearchFeaturePageShell
        pageId="settingsV2"
        error={localizeMessage('ui.searchSettings.searchSettingsPage.sessionUnavailable')}
        onRetry={() => { window.location.href = '/embedded/app'; }}
      />
    );
  }

  return (
    <SearchExperienceEditor
      client={effectiveClient}
      shopId={currentShop}
    />
  );
};

export default SearchExperienceV2SettingsPage;
